import React, { useEffect, useMemo, useRef, useState } from "react";
import BreadCrumbs from "src/components/BreadCrumbs";
import { Container, EmptyContainer, NoTicketsMessage, PaginationContainer } from "src/components/ListScreen";
import { Content, ContentFrame, TopBar } from "src/styles";
import { Spinner } from "react-bootstrap";
import { Pagination } from "src/components/Pagination";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { AttendanceLogsTable } from "./AttendanceLogsTable";
import type { AttendanceLogTableRow } from "./AttendanceLogsTable";
import { useIsMobile } from "src/hooks/useIsMobile";
import { ID } from "src/types";
import { FirebaseDataService } from "src/services/Firebase/data";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, useQueryParam } from "use-query-params";
import { setOpenedWashroomLogsPage as setPageStore } from "src/redux/actions/openedPages";
import {
  CLIENTS_TIME_AND_ATTENDANCE_LOGS_BREADCRUMBS,
  DEFAULT_PAGE_SIZE,
  INITIAL_LAST_PAGE,
  TIME_AND_ATTENDANCE_LOGS_BREADCRUMBS,
  TIME_AND_ATTENDANCE_LOGS_TABLE_HEADERS,
} from "./constants";
import { getUserData } from "src/redux/selectors/user";
import AttendanceLogsHeader from "./AttendanceLogsHeader";

type AttendanceApiItem = {
  id?: string;
  payPeriodDayId?: string;
  employeeId?: string;
  date?: string;
  clockInDate?: string | null;
  clockOutDate?: string | null;
};

const AttendanceLogs = () => {
  const { isClient, clientId: clientIdFromStore, firstName, lastName } = useSelector(getUserData);

  const { locationId: locationId } = useParams<{
    locationId: string;
  }>();

  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [attendanceLogs, setAttendanceLogs] = useState<AttendanceLogTableRow[]>([]);
  const [currentAttendanceLogsPage, setCurrentAttendanceLogsPage] = useState<AttendanceLogTableRow[]>([]);
  const [newDate, setNewDate] = useState<Date | null>(null);

  const datePickerRef = useRef<HTMLInputElement>(null);
  // const { id: employeeId } = useSelector(getUserData);

  const [isLoading, setIsLoading] = useState(false);

  const routeLocation = useLocation();
  const locationLabel: string = routeLocation.state?.locationLabel || "";

  const [openedTimeAndAttendanceLogsPage, setOpenedTimeAndAttendanceLogsPage] = useQueryParam(
    "openedTimeAndAttendanceLogsPage",
    NumberParam,
  );
  const [lastPage, setLastPage] = useState<number>(INITIAL_LAST_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const pageNum = openedTimeAndAttendanceLogsPage || 1;

  const filteredAttendanceLogs = useMemo(() => {
    if (!newDate || !moment(newDate).isValid()) {
      return attendanceLogs;
    }

    const selectedDate = moment(newDate).format("YYYY-MM-DD");
    return attendanceLogs.filter((log) => log.filterDate === selectedDate);
  }, [attendanceLogs, newDate]);

  const getPageSize = async () => {
    try {
      const { data } = await FirebaseDataService.getTimeAndAttendanceLogsPageSize();
      setPageSize(data || DEFAULT_PAGE_SIZE);
    } catch (e) {
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  };

  const getTimeAndAttendanceLogs = async () => {
    setIsLoading(true);
    if (!lastPage || (lastPage && pageNum <= lastPage)) {
      try {
        if (locationId && clientIdFromStore) {
          const { data } = await FirebaseDataService.getTimeAndAttendanceLogs(clientIdFromStore, locationId);

          const parsedAttendanceLogs = Array.isArray(data) ? (data as AttendanceApiItem[]) : [];
          const uniqueEmployeeIds = [
            ...new Set(
              parsedAttendanceLogs
                .map((log) => log.employeeId)
                .filter((employeeId): employeeId is string => !!employeeId),
            ),
          ];

          const employeeNameEntries = await Promise.allSettled(
            uniqueEmployeeIds.map(async (employeeId) => {
              const { data: employeeData } = await FirebaseDataService.getEmployeeById(employeeId);
              const fullName = `${employeeData?.firstName || ""} ${employeeData?.lastName || ""}`.trim();
              return [employeeId, fullName || employeeId] as const;
            }),
          );

          const employeeNameMap = employeeNameEntries.reduce<Record<string, string>>((acc, result) => {
            if (result.status === "fulfilled") {
              const [employeeId, fullName] = result.value;
              acc[employeeId] = fullName;
            }
            return acc;
          }, {});

          const attendanceRows: AttendanceLogTableRow[] = [...parsedAttendanceLogs]
            .sort((a, b) => {
              const aDate = a.date || a.clockInDate || a.clockOutDate || "";
              const bDate = b.date || b.clockInDate || b.clockOutDate || "";
              return moment(bDate).valueOf() - moment(aDate).valueOf();
            })
            .map((log, index) => {
              const dateValue = log.date || log.clockInDate || log.clockOutDate || "";
              const startValue = log.clockInDate || "";
              const endValue = log.clockOutDate || "";
              const parsedDate = moment(dateValue);

              return {
                id: log.id || log.payPeriodDayId || `${log.employeeId || "tm"}-${index}`,
                teamMember: (log.employeeId && employeeNameMap[log.employeeId]) || log.employeeId || "N/A",
                date: parsedDate.isValid() ? parsedDate.format("MMM D, YYYY") : "—",
                start: startValue ? moment(startValue).format("hh:mm A") : "—",
                end: endValue ? moment(endValue).format("hh:mm A") : "—",
                filterDate: parsedDate.isValid() ? parsedDate.format("YYYY-MM-DD") : "",
              };
            });

          setAttendanceLogs(attendanceRows);
        }
      } catch (error) {
        console.error("Error fetching time and attendance logs", error);
        setAttendanceLogs([]);
        setCurrentAttendanceLogsPage([]);
        setLastPage(0);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const totalCount = filteredAttendanceLogs.length;
    const computedLastPage = Math.ceil(totalCount / pageSize);
    setLastPage(computedLastPage);

    const safePageNumber = computedLastPage > 0 ? Math.min(pageNum, computedLastPage) : 1;

    if (safePageNumber !== pageNum) {
      setOpenedTimeAndAttendanceLogsPage(safePageNumber);
      dispatch(setPageStore(safePageNumber));
      return;
    }

    setCurrentAttendanceLogsPage(
      filteredAttendanceLogs.slice((safePageNumber - 1) * pageSize, safePageNumber * pageSize),
    );
  }, [dispatch, filteredAttendanceLogs, pageNum, pageSize, setOpenedTimeAndAttendanceLogsPage]);

  useEffect(() => {
    setIsLoading(true);
    getPageSize().finally(() => getTimeAndAttendanceLogs());
  }, [clientIdFromStore, locationId, pageSize]);

  const onAttendancePageChangeClick = (pageNumber: number) => {
    setOpenedTimeAndAttendanceLogsPage(pageNumber);
    dispatch(setPageStore(pageNumber));
    setCurrentAttendanceLogsPage(filteredAttendanceLogs.slice((pageNumber - 1) * pageSize, pageNumber * pageSize));
  };

  const onClickBreadcrumb = (route: ID | null) => {
    if (typeof route === "string") {
      // Ensure all placeholders are replaced before navigation
      let finalRoute = route;

      // Replace any remaining placeholders with actual values
      if (finalRoute.includes(":clientCode") && clientIdFromStore) {
        finalRoute = finalRoute.replace(":clientCode", clientIdFromStore);
      }
      if (finalRoute.includes(":locationId") && locationId) {
        finalRoute = finalRoute.replace(":locationId", locationId);
      }

      // Only navigate if no placeholders remain
      if (!finalRoute.includes(":")) {
        navigate(finalRoute);
      } else {
        console.warn("Cannot navigate: route contains unresolved placeholders:", finalRoute);
      }
    }
  };

  const clientBreadcrumbs = CLIENTS_TIME_AND_ATTENDANCE_LOGS_BREADCRUMBS.map((crumb) => {
    const updatedCrumb = { ...crumb };
    if (crumb.id.includes(":clientCode") && clientIdFromStore) {
      updatedCrumb.id = crumb.id.replace(":clientCode", clientIdFromStore);
    }
    if (crumb.name === ":clientCode" && clientIdFromStore) {
      updatedCrumb.name = `${firstName} ${lastName}`;
    }
    if (crumb.id.includes(":locationId") && locationId) {
      updatedCrumb.id = crumb.id.replace(":locationId", locationId);
    }
    if (crumb.name === ":locationId" && locationId) {
      updatedCrumb.name = locationLabel || locationId;
    }
    return updatedCrumb;
  });

  const breadcrumbs = TIME_AND_ATTENDANCE_LOGS_BREADCRUMBS.map((crumb) => {
    const updatedCrumb = { ...crumb };
    if (crumb.id.includes(":clientCode") && clientIdFromStore) {
      updatedCrumb.id = crumb.id.replace(":clientCode", clientIdFromStore);
    }
    if (crumb.name === ":clientCode" && clientIdFromStore) {
      updatedCrumb.name = `${firstName} ${lastName}`;
    }
    if (crumb.id.includes("user=client") && clientIdFromStore) {
      updatedCrumb.id = crumb.id.replace("user=client", `user=${clientIdFromStore}`);
    }
    if (crumb.name === ":clientCode" && clientIdFromStore) {
      updatedCrumb.name = `${firstName} ${lastName}`;
    }
    if (crumb.id.includes(":locationId") && locationId) {
      updatedCrumb.id = crumb.id.replace(":locationId", locationId);
    }
    if (crumb.name === ":locationId" && locationId) {
      updatedCrumb.name = locationLabel || locationId;
    }

    return updatedCrumb;
  });

  return (
    <Container>
      <TopBar>
        <BreadCrumbs folderStack={isClient ? clientBreadcrumbs : breadcrumbs} onClickItem={onClickBreadcrumb} />
      </TopBar>

      <Content>
        <ContentFrame>
          {isLoading ? (
            <EmptyContainer>
              <Spinner animation="border" size="sm" />
            </EmptyContainer>
          ) : (
            <>
              {attendanceLogs.length ? (
                <>
                  <AttendanceLogsHeader
                    datePickerRef={datePickerRef}
                    newDate={newDate}
                    setNewDate={setNewDate}
                    fieldName="date"
                    minDate={moment().subtract(2, "month").toDate()}
                  />
                  {currentAttendanceLogsPage?.length ? (
                    <AttendanceLogsTable
                      onRowClick={() => {}}
                      isLoading={isLoading}
                      headers={TIME_AND_ATTENDANCE_LOGS_TABLE_HEADERS}
                      data={currentAttendanceLogsPage}
                    />
                  ) : (
                    <NoTicketsMessage $isMobile={isMobile}>No time and attendance logs available</NoTicketsMessage>
                  )}
                </>
              ) : (
                <NoTicketsMessage $isMobile={isMobile}>No time and attendance logs available</NoTicketsMessage>
              )}
              <PaginationContainer>
                <Pagination
                  totalCount={filteredAttendanceLogs.length}
                  pageSize={pageSize}
                  onPageChange={onAttendancePageChangeClick}
                  currentPage={pageNum}
                />
              </PaginationContainer>
            </>
          )}
        </ContentFrame>
      </Content>
      <Outlet />
    </Container>
  );
};

export default AttendanceLogs;
